import { useSWR } from '@tiger/swr';
import { useEffect, useState } from 'react';
import { $Fieldutil, withField } from 'react-bootstrap-formutil';
import Toast from 'components/Toast';
import UI from 'stores/ui';
import { PicProofEnum } from 'types/common';
import API from 'utils/API';
import classlist from 'utils/classlist';
import http from 'utils/http';
import { getAccepts, isImageFile } from './helper';
import pdfFileImg from './images/pdf-file.png';
import type { AllowSizeOption, AllowUploadItemType, FileUploaderType } from './type';

import './fileInput.scss';

export interface FileInputProps {
    $fieldutil: $Fieldutil<null | {
        url: string;
        localUrl: string;
        fileName: string;
    }>;
    className?: string;
    field?: PicProofEnum;
    type: FileUploaderType;
    allow?: AllowUploadItemType;
    sizeOption?: AllowSizeOption;
    tooltip?: string;
}

const FileInput: React.FC<FileInputProps> = ({
    $fieldutil,
    className,
    allow,
    field,
    type,
    sizeOption,
    tooltip = __('请上传证件')
}) => {
    const { $value } = $fieldutil;
    const finalValue = $value?.url || ($value as unknown as string) || '';
    const finalAllow = allow || getAccepts(field, sizeOption);
    const [localUrl, setLocalUrl] = useState<string | null>(() => {
        const isPdf = ($value?.fileName || finalValue)?.endsWith('pdf');

        return isPdf ? pdfFileImg : $value?.localUrl || '';
    });
    const [isUploading, setIsUploading] = useState(false);
    const [statusText, setStatusText] = useState(() => {
        if (($value?.fileName || finalValue)?.endsWith('pdf')) {
            return $value?.fileName;
        }

        return tooltip;
    });
    const [progress, setProgress] = useState(0);
    const [showUploadIcon, setShowUploadIcon] = useState(true);
    const hasLocalUrl = !!(finalValue && localUrl);
    const { data: dataLocalUrl } = useSWR(
        !localUrl && isImageFile(finalValue) ? API.CUSTOMER.getImgUrl(finalValue) : null,
        {
            fetcher: config =>
                http.get(config.url, {
                    responseType: 'arraybuffer'
                })
        }
    );

    useEffect(() => {
        if (dataLocalUrl) {
            const blob = new Blob([dataLocalUrl]);
            const url = URL.createObjectURL(blob);

            setLocalUrl(url);
            setStatusText('');
            setShowUploadIcon(false);
        }
    }, [dataLocalUrl]);

    useEffect(
        () => () => {
            if (localUrl) {
                URL.revokeObjectURL(localUrl);
            }
        },
        [localUrl]
    );

    const onClick = (ev: React.MouseEvent) => {
        ev.preventDefault();

        const input = document.createElement('input');

        input.type = 'file';
        input.accept = finalAllow.accepts.map(item => item.accept).join(',');

        input.onchange = fileEvent => {
            // @ts-ignore
            const { files } = fileEvent.target;

            if (files.length) {
                const file = files[0];

                for (const item of finalAllow.accepts) {
                    const acceptReg = new RegExp(item.accept, 'i');

                    // 如果文件类型能够匹配accept或者后缀可以匹配到允许的ext,就对图片做出改变
                    if (acceptReg.test(file.type) || item.ext.some(ext => file.name.endsWith(ext))) {
                        fileChange(file).finally(() => {
                            document.body.removeChild(input);
                        });

                        return;
                    }
                }

                Toast.show(finalAllow.allowText || __('不支持的文件类型'));
            }

            document.body.removeChild(input);
        };

        // ios上，input必须插入到页面dom树中，才能触发onchange事件
        input.style.display = 'none';
        document.body.appendChild(input);

        input.click();
    };

    const fileChange = async file => {
        setIsUploading(true);

        try {
            // 目前只允许上传图片或pdf文件
            const isImage = file.type?.startsWith('image');
            const tempLocalUrl = isImage ? await readDataUrlFromFile(file) : pdfFileImg;

            // 压缩图片尺寸
            file = await compress(file);

            // 存在可同时上传多种文件类型的情况，需要根据不同文件类型具体是否有大小限制要求来判断
            const {
                min: minSize = 0,
                max: maxSize,
                tip: sizeTip
            } = finalAllow.accepts.find(item => new RegExp(item.accept, 'i').test(file.type))?.sizeOption || {};

            // 证件图片大小限制在32k-10M之间： https://ones.tigerbrokers.net/project/#/team/XwPpCNX9/task/MY6pfgZfbeqWKu83
            if (file.size >= minSize && (!maxSize || file.size <= maxSize)) {
                setLocalUrl(tempLocalUrl);
                setStatusText(isImage ? '' : file.name);
                setShowUploadIcon(false);

                await uploadFile(file);
            } else {
                sizeTip && Toast.show(sizeTip);
            }
        } catch (error) {
            Toast.show(error, 3000);
        }

        setIsUploading(false);
    };

    const uploadFile = async file => {
        const formdata = new FormData();

        formdata.append('file', file, file.name);

        try {
            const resp = await http.post(API.CUSTOMER.uploadFile(), formdata, {
                useJson: true,
                timeout: 60000,
                onUploadProgress: ev => {
                    setProgress(Math.floor((ev.loaded / ev.total) * 100));
                }
            });

            $fieldutil.$render!({
                url: resp.data.url,
                localUrl: localUrl,
                fileName: file.name
            });
        } catch (error) {
            Toast.show(error);

            // 上传失败，清除本地预览
            setLocalUrl(null);
            setStatusText(tooltip);
        }
    };

    /**
     * 读取本地文件获得预览地址
     * @param {FILE} file 文件对象
     *
     * @return {Promise}
     */
    const readDataUrlFromFile = async (file): Promise<string> => {
        try {
            const createObjectURL =
                // @ts-ignore
                window.createObjectURL || window.URL.createObjectURL || window.webkitURL.createObjectURL;

            return createObjectURL(file);
        } catch (e) {
            //
        }

        return '';
    };

    /**
     * 压缩图片尺寸
     * @param {FILE} file 要压缩的图片文件
     * @return {Promise}
     *
     * ios系统上貌似超过2m的图片会写入canvas失败，所以压缩这一步貌似不行？？？
     */
    const compress = (file: File) => {
        return new Promise((resolve, reject) => {
            // 如果浏览器不支持相关接口，就返回原文件
            // @ts-ignore
            if (!window.FileReader) {
                return resolve(file);
            }

            // 非图片直接返回原文件
            if (!file.type?.startsWith('image')) {
                return resolve(file);
            }

            const rejectImage = () => {
                reject(new Error(__('该图片可能已经损坏，请选择其他图片。')));

                setLocalUrl(null);
            };

            const filename = file.name.split('.').slice(0, -1).join('.');

            const cimage = new Image();
            const fileReader = new FileReader();

            fileReader.onload = ev => {
                // @ts-ignore
                cimage.src = ev.target.result!;
            };

            fileReader.onerror = () => {
                rejectImage();
            };

            fileReader.readAsDataURL(file);

            const MAX_SIZE = 2000;

            cimage.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d')!;
                const imgRatio = cimage.height / cimage.width;

                // 超长、超宽，或者图片大于2M 才进行压缩
                if ((cimage.height < MAX_SIZE && cimage.width < MAX_SIZE) || file.size <= 2 * 1024 * 1024) {
                    return resolve(file);
                }

                if (imgRatio < 1) {
                    canvas.width = MAX_SIZE;
                    canvas.height = MAX_SIZE * imgRatio;
                } else {
                    canvas.height = MAX_SIZE;
                    canvas.width = MAX_SIZE / imgRatio;
                }

                // 居中将签名写入画布
                ctx.drawImage(cimage, 0, 0, canvas.width, canvas.height);

                canvas.toBlob((pic: any) => {
                    pic.name = `${filename}.jpg`;
                    resolve(pic);
                }, 'image/jpeg');
            };

            cimage.onerror = () => {
                rejectImage();
            };
        });
    };

    return (
        <div
            className={classlist(
                'file-input-component',
                `file-input-${type}`,
                {
                    'hide-bg': hasLocalUrl
                },
                className
            )}
            onMouseEnter={() => hasLocalUrl && setShowUploadIcon(true)}
            onMouseLeave={() => hasLocalUrl && setShowUploadIcon(false)}>
            <button className="file-select-trigger" onClick={onClick} type="button" />
            {/* 移动端或者需要展示上传图标时展示图标，但上传中始终不展示 */}
            {(UI.isMobile || showUploadIcon) && !isUploading && <div className="upload-icon"></div>}
            {isUploading && (
                <div className="upload-bar">
                    <div className="process-bar">
                        <div className="upload-progress" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="process-text">{`${progress} %`}</span>
                </div>
            )}
            {statusText && (
                <div className="status-bar">
                    <span className={classlist('status-text')}>{statusText}</span>
                </div>
            )}
            {localUrl && <img src={localUrl} className="preview" alt={$value?.fileName} />}
        </div>
    );
};

export default withField(FileInput, {
    $defaultValue: null,
    $validators: {
        required: value => !!value || __('请上传证件')
    }
});
